import React, { useState, useEffect } from 'react';
import { bool, arrayOf, number, shape } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { Suspense } from 'react';
import classNames from 'classnames';
import defaultConfig from '../../config/configDefault';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import { REVIEW_TYPE_OF_PROVIDER, REVIEW_TYPE_OF_CUSTOMER, propTypes } from '../../util/types';
import { ensureCurrentUser, ensureUser } from '../../util/data';
import { withViewport } from '../../util/uiHelpers';
import { useConfiguration } from '../../context/configurationContext';
import {
  SCHEMA_TYPE_MULTI_ENUM,
  SCHEMA_TYPE_TEXT,
  SCHEMA_TYPE_YOUTUBE,
} from '../../util/types';
import { useIntl } from '../../util/reactIntl';
import {
  NO_ACCESS_PAGE_USER_PENDING_APPROVAL,
  NO_ACCESS_PAGE_VIEW_LISTINGS,
  PROFILE_PAGE_PENDING_APPROVAL_VARIANT,
} from '../../util/urlHelpers';
import {
  isErrorNoViewingPermission,
  isErrorUserPendingApproval,
  isForbiddenError,
  isNotFoundError,
} from '../../util/errors';
import { pickCustomFieldProps } from '../../util/fieldHelpers';
import { hasPermissionToViewData, isUserAuthorized } from '../../util/userHelpers';
import { richText } from '../../util/richText';
import { isScrollingDisabled } from '../../ducks/ui.duck';
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { getFieldValue } from '../../util/fieldHelpers';
import {
  Heading,
  H2,
  H4,
  Page,
  AvatarLarge,
  NamedLink,
  ListingCard,
  Reviews,
  ButtonTabNavHorizontal,
  LayoutSideNavigation,
  LayoutSingleColumn,
  NamedRedirect,
} from '../../components';

import TopbarContainer from '../../containers/TopbarContainer/TopbarContainer';
import FooterContainer from '../../containers/FooterContainer/FooterContainer';
import NotFoundPage from '../../containers/NotFoundPage/NotFoundPage';

const EndorsementComponent = React.lazy(() => import('../../components/Endorsements/Endorsements'));

import { THEMES, themeToVars } from './themes';
import ProfileShareQR from './ProfileShareQR';
import useVerifiedStatus from '../../hooks/useVerifiedStatus';
import VerifiedBadge from '../../components/VerifiedBadge/VerifiedBadge';
import css from './ProfilePage.module.css';
import SectionDetailsMaybe from './SectionDetailsMaybe';
import SectionTextMaybe from './SectionTextMaybe';
import SectionMultiEnumMaybe from './SectionMultiEnumMaybe';
import SectionYoutubeVideoMaybe from './SectionYoutubeVideoMaybe';

const MAX_MOBILE_SCREEN_WIDTH = 768;

const FONT_GFONTS_URL =
  'https://fonts.googleapis.com/css2?' +
  'family=Hanken+Grotesk:wght@400;700;800&' +
  'family=Inter:wght@400;600;800&' +
  'family=Poppins:wght@400;600;800&' +
  'family=Nunito:wght@400;700;800&' +
  'family=Raleway:wght@400;700;800&' +
  'family=Outfit:wght@400;700;800&' +
  'family=Plus+Jakarta+Sans:wght@400;600;800&' +
  'family=Montserrat:wght@400;700;800&' +
  'family=Merriweather:wght@400;700&' +
  'family=EB+Garamond:wght@400;700&' +
  'family=Lora:wght@400;700&' +
  'family=Cormorant+Garamond:wght@400;700&' +
  'family=Playfair+Display:wght@700;800&' +
  'family=DM+Serif+Display&' +
  'family=Libre+Baskerville:wght@400;700&' +
  'family=Bebas+Neue&' +
  'family=Oswald:wght@400;700&' +
  'family=Bricolage+Grotesque:wght@400;700;800&' +
  'family=Dancing+Script:wght@400;700&' +
  'family=Pacifico&' +
  'family=Caveat:wght@400;700&' +
  'family=Great+Vibes&' +
  'family=Space+Mono:wght@400;700&' +
  'family=IBM+Plex+Mono:wght@400;700&' +
  'family=Special+Elite&' +
  'family=Courier+Prime:wght@400;700&' +
  'display=swap';
const MIN_LENGTH_FOR_LONG_WORDS = 20;

const DEFAULT_BRANDING = {
  layout: null,
  primaryColor: null,
  accentColor: null,
  bannerUrl: null,
  logoUrl: null,
  tabOrder: ['bio', 'listings', 'reviews', 'results'],
  socialLinks: {},
  headline: null,
  headlineColor: null,
  story: null,
  font: null,
  showFooter: true,
  heroStyle: null,
  heroColor: null,
  heroImageUrl: null,
  showName: true,
  showHeadline: true,
  metaBarPosition: 'top',
  logoSize: 'md',
  headingFontBold: false,
  buttonTextColor: null,
};

const LOGO_SIZES = { sm: 64, md: 96, lg: 140, xl: 190 };
const logoSizeStyle = size => {
  const px = LOGO_SIZES[size] || LOGO_SIZES.md;
  return { width: px, height: px, maxWidth: px, maxHeight: px };
};

const heroBgFromBranding = b => {
  if (b.heroStyle === 'image' && b.heroImageUrl) return { backgroundImage: `url(${b.heroImageUrl})` };
  if (b.heroStyle === 'color' && b.heroColor) return { background: b.heroColor };
  if (b.bannerUrl) return { backgroundImage: `url(${b.bannerUrl})` };
  if (b.coverGradient) return { background: b.coverGradient };
  return {};
};

const SOCIAL_ICONS = {
  instagram: (
    <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20" aria-hidden="true">
      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
    </svg>
  ),
  twitter: (
    <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20" aria-hidden="true">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.748l7.73-8.835L1.254 2.25H8.08l4.259 5.632L18.244 2.25zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77z"/>
    </svg>
  ),
  x: (
    <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20" aria-hidden="true">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.748l7.73-8.835L1.254 2.25H8.08l4.259 5.632L18.244 2.25zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77z"/>
    </svg>
  ),
  facebook: (
    <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20" aria-hidden="true">
      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
    </svg>
  ),
  youtube: (
    <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20" aria-hidden="true">
      <path d="M23.495 6.205a3.007 3.007 0 0 0-2.088-2.088c-1.87-.501-9.396-.501-9.396-.501s-7.507-.01-9.396.501A3.007 3.007 0 0 0 .527 6.205a31.247 31.247 0 0 0-.522 5.805 31.247 31.247 0 0 0 .522 5.783 3.007 3.007 0 0 0 2.088 2.088c1.868.502 9.396.502 9.396.502s7.506 0 9.396-.502a3.007 3.007 0 0 0 2.088-2.088 31.247 31.247 0 0 0 .5-5.783 31.247 31.247 0 0 0-.5-5.805zM9.609 15.601V8.408l6.264 3.602z"/>
    </svg>
  ),
  tiktok: (
    <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20" aria-hidden="true">
      <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
    </svg>
  ),
  linkedin: (
    <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20" aria-hidden="true">
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
    </svg>
  ),
};

const SocialLinks = ({ socialLinks, vertical = false }) => {
  if (!socialLinks) return null;
  const links = Object.entries(socialLinks).filter(([, url]) => url);
  if (!links.length) return null;
  return (
    <div className={vertical ? css.socialLinksVertical : css.socialLinks}>
      {links.map(([platform, url]) => (
        <a
          key={platform}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className={vertical ? css.socialIconVertical : css.socialIcon}
          aria-label={platform}
          title={platform}
        >
          {SOCIAL_ICONS[platform]}
          {vertical && <span className={css.socialIconLabel}>{platform.charAt(0).toUpperCase() + platform.slice(1).replace('twitter', 'X / Twitter')}</span>}
        </a>
      ))}
    </div>
  );
};

export const AsideContent = props => {
  const { user, displayName, showLinkToProfileSettingsPage, branding, profileUrl } = props;
  return (
    <div className={css.asideContent}>
      {branding?.logoUrl ? (
        <img src={branding.logoUrl} alt={displayName} className={css.customLogo} />
      ) : (
        <AvatarLarge className={css.avatar} user={user} disableProfileLink />
      )}
      <H2 as="h1" className={css.mobileHeading}>
        {displayName ? (
          <FormattedMessage id="ProfilePage.mobileHeading" values={{ name: displayName }} />
        ) : null}
      </H2>
      <SocialLinks socialLinks={branding?.socialLinks} />
      {profileUrl && <ProfileShareQR profileUrl={profileUrl} />}
      {showLinkToProfileSettingsPage ? (
        <>
          <NamedLink className={css.editLinkMobile} name="ProfileSettingsPage">
            <FormattedMessage id="ProfilePage.editProfileLinkMobile" />
          </NamedLink>
          <NamedLink className={css.editLinkDesktop} name="ProfileSettingsPage">
            <FormattedMessage id="ProfilePage.editProfileLinkDesktop" />
          </NamedLink>
          <NamedLink className={css.brandingLink} name="BrandingSettingsPage">
            ← Edit Branding
          </NamedLink>
        </>
      ) : null}
    </div>
  );
};

export const ReviewsErrorMaybe = props => {
  const { queryReviewsError } = props;
  return queryReviewsError ? (
    <p className={css.error}>
      <FormattedMessage id="ProfilePage.loadingReviewsFailed" />
    </p>
  ) : null;
};

export const MobileReviews = props => {
  const { reviews, queryReviewsError } = props;
  const reviewsOfProvider = reviews.filter(r => r.attributes.type === REVIEW_TYPE_OF_PROVIDER);
  const reviewsOfCustomer = reviews.filter(r => r.attributes.type === REVIEW_TYPE_OF_CUSTOMER);
  return (
    <div className={css.mobileReviews}>
      <H4 as="h2" className={css.mobileReviewsTitle}>
        <FormattedMessage
          id="ProfilePage.reviewsFromMyCustomersTitle"
          values={{ count: reviewsOfProvider.length }}
        />
      </H4>
      <ReviewsErrorMaybe queryReviewsError={queryReviewsError} />
      <Reviews reviews={reviewsOfProvider} />
      <H4 as="h2" className={css.mobileReviewsTitle}>
        <FormattedMessage
          id="ProfilePage.reviewsAsACustomerTitle"
          values={{ count: reviewsOfCustomer.length }}
        />
      </H4>
      <ReviewsErrorMaybe queryReviewsError={queryReviewsError} />
      <Reviews reviews={reviewsOfCustomer} />
    </div>
  );
};

export const DesktopReviewTabs = props => {
  const { reviews, queryReviewsError } = props;
  const [activeTab, setActiveTab] = useState(REVIEW_TYPE_OF_PROVIDER);
  const reviewsOfProvider = reviews.filter(r => r.attributes.type === REVIEW_TYPE_OF_PROVIDER);
  const reviewsOfCustomer = reviews.filter(r => r.attributes.type === REVIEW_TYPE_OF_CUSTOMER);

  const tabs = [
    {
      text: (
        <Heading as="h3" rootClassName={css.desktopReviewsTitle}>
          <FormattedMessage id="ProfilePage.reviewsFromMyCustomersTitle" values={{ count: reviewsOfProvider.length }} />
        </Heading>
      ),
      selected: activeTab === REVIEW_TYPE_OF_PROVIDER,
      onClick: () => setActiveTab(REVIEW_TYPE_OF_PROVIDER),
    },
    {
      text: (
        <Heading as="h3" rootClassName={css.desktopReviewsTitle}>
          <FormattedMessage id="ProfilePage.reviewsAsACustomerTitle" values={{ count: reviewsOfCustomer.length }} />
        </Heading>
      ),
      selected: activeTab === REVIEW_TYPE_OF_CUSTOMER,
      onClick: () => setActiveTab(REVIEW_TYPE_OF_CUSTOMER),
    },
  ];

  return (
    <div className={css.desktopReviews}>
      <div className={css.desktopReviewsWrapper}>
        <ButtonTabNavHorizontal className={css.desktopReviewsTabNav} tabs={tabs} />
        <ReviewsErrorMaybe queryReviewsError={queryReviewsError} />
        {activeTab === REVIEW_TYPE_OF_PROVIDER && <Reviews reviews={reviewsOfProvider} />}
        {activeTab === REVIEW_TYPE_OF_CUSTOMER && <Reviews reviews={reviewsOfCustomer} />}
      </div>
    </div>
  );
};

export const CustomUserFields = props => {
  const { publicData, metadata, userFieldConfig } = props;

  const shouldPickUserField = fieldConfig => fieldConfig?.showConfig?.displayInProfile !== false;
  const propsForCustomFields =
    pickCustomFieldProps(publicData, metadata, userFieldConfig, 'userType', shouldPickUserField) ||
    [];

  return (
    <>
      <SectionDetailsMaybe {...props} />
      {propsForCustomFields.map(customFieldProps => {
        const { schemaType, key, ...fieldProps } = customFieldProps;
        return schemaType === SCHEMA_TYPE_MULTI_ENUM ? (
          <SectionMultiEnumMaybe key={key} {...fieldProps} />
        ) : schemaType === SCHEMA_TYPE_TEXT ? (
          <SectionTextMaybe key={key} {...fieldProps} />
        ) : schemaType === SCHEMA_TYPE_YOUTUBE ? (
          <SectionYoutubeVideoMaybe key={key} {...fieldProps} />
        ) : null;
      })}
    </>
  );
};

/* ── Profile metadata bar (gender / location / experience / training mode) ── */
const IN_PERSON_VALUES = new Set(['in_person', 'in-person', 'in person', 'inperson', 'offline']);
const VIRTUAL_VALUES = new Set(['virtual', 'online', 'remote', 'digital']);

const StarRating = ({ rating, count }) => {
  if (!rating) return null;
  const full = Math.floor(rating);
  const half = rating - full >= 0.4;
  const stars = [];
  for (let i = 0; i < 5; i++) {
    const filled = i < full ? 1 : i === full && half ? 0.5 : 0;
    stars.push(
      <svg key={i} viewBox="0 0 24 24" width="14" height="14" aria-hidden="true"
        style={{ fill: filled === 1 ? '#f59e0b' : filled === 0.5 ? 'url(#half)' : 'none', stroke: '#f59e0b', strokeWidth: 1.5 }}>
        {filled === 0.5 && (
          <defs>
            <linearGradient id="half"><stop offset="50%" stopColor="#f59e0b"/><stop offset="50%" stopColor="transparent"/></linearGradient>
          </defs>
        )}
        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
      </svg>
    );
  }
  return (
    <span className={css.metaItem} style={{ gap: 2 }}>
      {stars}
      <span style={{ fontSize: 12, fontWeight: 600, marginLeft: 3 }}>{rating.toFixed(1)}</span>
      {count > 0 && <span style={{ fontSize: 11, color: 'var(--colorGrey500)', marginLeft: 2 }}>({count})</span>}
    </span>
  );
};

const ProfileMetaBar = ({ publicData, userFieldConfig, avgRating, reviewCount, isVerified }) => {
  if (!publicData) return null;

  const gender = getFieldValue(publicData, 'gender');
  const yearsExp = getFieldValue(publicData, 'years_experience') || getFieldValue(publicData, 'experience');
  const location = getFieldValue(publicData, 'userLocation');

  // Resolve enum label from config
  const getEnumLabel = (key, value) => {
    if (!userFieldConfig || !value) return value;
    const field = userFieldConfig.find(f => f.key === key);
    if (!field || field.schemaType !== 'enum') return value;
    const opt = (field.enumOptions || []).find(o => o.option === value);
    return opt?.label || value;
  };

  // Find training format: check config for fields with in-person/virtual options
  let showInPerson = false;
  let showVirtual = false;
  if (userFieldConfig) {
    for (const field of userFieldConfig) {
      const opts = field.enumOptions || [];
      const hasTrainingOpts = opts.some(o =>
        IN_PERSON_VALUES.has(o.option?.toLowerCase()) || VIRTUAL_VALUES.has(o.option?.toLowerCase())
      );
      if (!hasTrainingOpts) continue;
      const val = publicData[field.key];
      if (!val) continue;
      const vals = (Array.isArray(val) ? val : [val]).map(v => v?.toLowerCase?.());
      if (vals.some(v => IN_PERSON_VALUES.has(v))) showInPerson = true;
      if (vals.some(v => VIRTUAL_VALUES.has(v))) showVirtual = true;
    }
  }
  // Fallback: check common keys directly
  if (!showInPerson && !showVirtual) {
    const KEYS = ['trainingFormat', 'training_format', 'sessionFormat', 'workMode', 'sessionType', 'serviceMode'];
    for (const key of KEYS) {
      const val = publicData[key];
      if (!val) continue;
      const vals = (Array.isArray(val) ? val : [val]).map(v => v?.toLowerCase?.());
      if (vals.some(v => IN_PERSON_VALUES.has(v))) showInPerson = true;
      if (vals.some(v => VIRTUAL_VALUES.has(v))) showVirtual = true;
      if (showInPerson || showVirtual) break;
    }
  }

  const hasAnything = gender || yearsExp || location || showInPerson || showVirtual || avgRating || isVerified;
  if (!hasAnything) return null;

  return (
    <div className={css.metaBar}>
      {avgRating && <StarRating rating={avgRating} count={reviewCount || 0} />}
      {location && (
        <span className={css.metaItem}>
          <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14" aria-hidden="true"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/></svg>
          {getEnumLabel('userLocation', location) || location}
        </span>
      )}
      {gender && (
        <span className={css.metaItem}>
          <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14" aria-hidden="true"><path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/></svg>
          {getEnumLabel('gender', gender) || gender}
        </span>
      )}
      {yearsExp && (
        <span className={css.metaItem}>
          <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14" aria-hidden="true"><path d="M13.49 5.48c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm-3.6 13.9l1-4.4 2.1 2v6h2v-7.5l-2.1-2 .6-3c1.3 1.5 3.3 2.5 5.5 2.5v-2c-1.9 0-3.5-1-4.3-2.4l-1-1.6c-.4-.6-1-1-1.7-1-.3 0-.5.1-.8.1l-5.2 2.2v4.7h2v-3.4l1.8-.7-1.6 8.1-4.9-1-.4 2 7 1.4z"/></svg>
          {yearsExp} yrs experience
        </span>
      )}
      {(showInPerson || showVirtual) && (
        <span className={css.metaItem}>
          {showInPerson && (
            <span className={css.trainingBadge}>
              <svg viewBox="0 0 24 24" fill="currentColor" width="13" height="13" aria-hidden="true"><path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/></svg>
              In-person
            </span>
          )}
          {showVirtual && (
            <span className={css.trainingBadge}>
              <svg viewBox="0 0 24 24" fill="currentColor" width="13" height="13" aria-hidden="true"><path d="M17 10.5V7c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4z"/></svg>
              Virtual
            </span>
          )}
        </span>
      )}
      {isVerified && <VerifiedBadge size="sm" showLabel label="Background Checked" />}
    </div>
  );
};

const BioTabContent = props => {
  const { displayName, publicData, metadata, userFieldConfig, intl } = props;
  return (
    <div>
      {displayName ? (
        <CustomUserFields
          publicData={publicData}
          metadata={metadata}
          userFieldConfig={userFieldConfig}
          intl={intl}
        />
      ) : null}
    </div>
  );
};

const ListingsTabContent = ({ listings, queryListingsError }) => {
  if (queryListingsError) {
    return <p className={css.error}><FormattedMessage id="ProfilePage.loadingDataFailed" /></p>;
  }
  if (!listings.length) return <p className={css.emptyState}>No listings yet.</p>;
  return (
    <div className={css.listingsContainer}>
      <ul className={css.listings}>
        {listings.map(l => (
          <li className={css.listing} key={l.id.uuid}>
            <ListingCard listing={l} showAuthorInfo={false} showVerifiedBadge={false} />
          </li>
        ))}
      </ul>
    </div>
  );
};

const ReviewsTabContent = ({ reviews, queryReviewsError, isMobileLayout, endorsementCount, uuid }) => {
  return (
    <div>
      {isMobileLayout ? (
        <MobileReviews reviews={reviews} queryReviewsError={queryReviewsError} />
      ) : (
        <DesktopReviewTabs reviews={reviews} queryReviewsError={queryReviewsError} />
      )}
      <div className={css.desktopEndorsements}>
        <H4 as="h2" className={css.endorsementsTitle}>
          <FormattedMessage id="ProfilePage.endorsementsTitle" values={{ count: endorsementCount }} />
        </H4>
        <Suspense fallback={<div>Loading endorsements...</div>}>
          <EndorsementComponent uuid={uuid} />
        </Suspense>
      </div>
    </div>
  );
};

const ResultsTabContent = ({ uuid }) => {
  const [photos, setPhotos] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!uuid) return;
    fetch(`/api/result-photos/public/${uuid}`)
      .then(r => r.json())
      .then(data => { if (data.photos) setPhotos(data.photos); })
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, [uuid]);

  if (!loaded) return null;
  if (!photos.length) return <p className={css.emptyState}>No result photos yet.</p>;

  return (
    <div className={css.resultPhotosGrid}>
      {photos.map(p => (
        <div key={p.id} className={css.resultPhotoCard}>
          <img src={p.photoUrl} alt={p.caption || 'Client result'} className={css.resultPhotoImg} />
          {p.caption && <p className={css.resultPhotoCaption}>{p.caption}</p>}
        </div>
      ))}
    </div>
  );
};

export const TabbedMainContent = props => {
  const [mounted, setMounted] = useState(false);
  const [activeProfileTab, setActiveProfileTab] = useState(null);

  useEffect(() => { setMounted(true); }, []);

  const {
    userShowError,
    bio,
    displayName,
    listings,
    uuid,
    queryListingsError,
    reviews = [],
    queryReviewsError,
    viewport,
    user,
    publicData,
    metadata,
    userFieldConfig,
    intl,
    hideReviews,
    branding,
    avgRating,
    reviewCount,
  } = props;

  const tabOrder = branding?.tabOrder || ['bio', 'listings', 'reviews'];
  const firstTab = activeProfileTab || tabOrder[0];

  const hasMatchMedia = typeof window !== 'undefined' && window?.matchMedia;
  const isMobileLayout = mounted && hasMatchMedia
    ? window.matchMedia(`(max-width: ${MAX_MOBILE_SCREEN_WIDTH}px)`)?.matches
    : true;

  if (userShowError || queryListingsError) {
    return <p className={css.error}><FormattedMessage id="ProfilePage.loadingDataFailed" /></p>;
  }

  const TAB_LABELS = { bio: 'About', listings: 'Train with Me', reviews: 'Reviews', results: 'Results' };

  const profileTabs = tabOrder
    .filter(t => t !== 'reviews' || !hideReviews)
    .map(tab => ({
      text: <span>{TAB_LABELS[tab] || tab}</span>,
      selected: firstTab === tab,
      onClick: () => setActiveProfileTab(tab),
    }));

  const isHeroLayout = branding?.layout === 'hero';
  const isMinimalLayout = branding?.layout === 'minimal';

  const headingText = branding?.headline || intl.formatMessage(
    { id: 'ProfilePage.desktopHeading' },
    { name: displayName }
  );

  const hasBio = !!bio;
  const bioWithLinks = hasBio
    ? richText(bio, { linkify: true, longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS, longWordClass: css.longWord })
    : null;

  return (
    <div>
      {/* Heading and meta bar are already rendered by HeroLayout/MinimalLayout */}
      {!isHeroLayout && !isMinimalLayout && (
        <>
          <div className={css.heroNameRow}>
            <H2 as="h1" className={css.desktopHeading}>{headingText}</H2>
            {props.isVerified && <VerifiedBadge size="md" showLabel />}
          </div>
          <ProfileMetaBar publicData={publicData} userFieldConfig={userFieldConfig} avgRating={avgRating} reviewCount={reviewCount} isVerified={props.isVerified} />
        </>
      )}
      {/* Bio always visible above the tabs, under the headline */}
      {hasBio && <p className={`${css.bio} ${css.bioAboveTabs}`}>{bioWithLinks}</p>}
      <StoryBlock story={branding?.story} />
      <ButtonTabNavHorizontal className={css.profileTabNav} tabs={profileTabs} />
      <div className={css.profileTabContent}>
        {firstTab === 'bio' && (
          <BioTabContent
            bio={bio}
            displayName={displayName}
            publicData={publicData}
            metadata={metadata}
            userFieldConfig={userFieldConfig}
            intl={intl}
          />
        )}
        {firstTab === 'listings' && (
          <ListingsTabContent listings={listings} queryListingsError={queryListingsError} />
        )}
        {firstTab === 'reviews' && !hideReviews && (
          <ReviewsTabContent
            reviews={reviews}
            queryReviewsError={queryReviewsError}
            isMobileLayout={isMobileLayout}
            endorsementCount={props.endorsementCount}
            uuid={uuid}
          />
        )}
        {firstTab === 'results' && <ResultsTabContent uuid={uuid} />}
      </div>
    </div>
  );
};

export const MainContent = props => {
  const [mounted, setMounted] = useState(false);
  const [activeTab, setActiveTab] = useState(REVIEW_TYPE_OF_PROVIDER);
  useEffect(() => {
    setMounted(true);
  }, []);

  const {
    userShowError,
    endorsementCount,
    bio,
    displayName,
    listings,
    uuid,
    queryListingsError,
    reviews = [],
    queryReviewsError,
    viewport,
    user,
    publicData,
    metadata,
    userFieldConfig,
    intl,
    hideReviews,
  } = props;

  const hasListings = listings.length > 0;
  const hasMatchMedia = typeof window !== 'undefined' && window?.matchMedia;
  const isMobileLayout =
    mounted && hasMatchMedia
      ? window.matchMedia(`(max-width: ${MAX_MOBILE_SCREEN_WIDTH}px)`)?.matches
      : true;

  const hasBio = !!bio;
  const bioWithLinks = richText(bio, {
    linkify: true,
    longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
    longWordClass: css.longWord,
  });

  const listingsContainerClasses = classNames(css.listingsContainer, {
    [css.withBioMissingAbove]: !hasBio,
  });

  if (userShowError || queryListingsError) {
    return (
      <p className={css.error}>
        <FormattedMessage id="ProfilePage.loadingDataFailed" />
      </p>
    );
  }
  return (
    <div>
      <H2 as="h1" className={css.desktopHeading}>
        <FormattedMessage id="ProfilePage.desktopHeading" values={{ name: displayName }} />
      </H2>
      {hasBio ? <p className={css.bio}>{bioWithLinks}</p> : null}

      {displayName ? (
        <CustomUserFields
          publicData={publicData}
          metadata={metadata}
          userFieldConfig={userFieldConfig}
          intl={intl}
        />
      ) : null}

      {hasListings ? (
        <div className={listingsContainerClasses}>
          <H4 as="h2" className={css.listingsTitle}>
            <FormattedMessage id="ProfilePage.listingsTitle" values={{ count: listings.length }} />
          </H4>
          <ul className={css.listings}>
            {listings.map(l => (
              <li className={css.listing} key={l.id.uuid}>
                <ListingCard listing={l} showAuthorInfo={false} showVerifiedBadge={false} />
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {hideReviews ? null : isMobileLayout ? (
        <MobileReviews reviews={reviews} queryReviewsError={queryReviewsError} />
      ) : (
        <DesktopReviewTabs
          user={user}
          reviews={reviews}
          queryReviewsError={queryReviewsError}
        />
      )}
      <div className={css.desktopEndorsements}>
        <H4 as="h2" className={css.endorsementsTitle}>
          <FormattedMessage id="ProfilePage.endorsementsTitle" values={{ count: endorsementCount }} />
        </H4>
        <Suspense fallback={<div>Loading endorsements...</div>}>
          <EndorsementComponent uuid={uuid} />
        </Suspense>
      </div>
    </div>
  );
};

/* ── Story block ──────────────────────────────────────────────────────── */
const StoryBlock = ({ story }) => {
  if (!story) return null;
  return (
    <blockquote className={css.storyBlock}>
      <span className={css.storyQuoteMark}>"</span>
      {story}
      <span className={css.storyQuoteMark}>"</span>
    </blockquote>
  );
};

/* ── Minimal layout ───────────────────────────────────────────────────── */
const MinimalLayout = props => {
  const { branding, displayName, profileUser, showLinkToProfileSettingsPage, profileUrl, publicData, userFieldConfig, avgRating, reviewCount, isVerified, children } = props;
  const showHeadline = branding.showHeadline !== false;
  const showName = branding.showName !== false;
  const metaBarPos = branding.metaBarPosition || 'top';
  const effectiveHeadline = showHeadline ? branding.headline : null;
  const heroHeading = effectiveHeadline || (showName ? displayName : null);
  const headingStyle = branding.headingFontBold ? { fontWeight: 700 } : {};
  const metaBar = <ProfileMetaBar publicData={publicData} userFieldConfig={userFieldConfig} isVerified={isVerified} avgRating={avgRating} reviewCount={reviewCount} />;
  return (
    <div className={css.minimalRoot}>
      <div className={css.minimalHeader}>
        <div className={css.logoContainer} style={{ ...logoSizeStyle(branding.logoSize), marginBottom: 16, borderRadius: '50%', overflow: 'hidden' }}>
          {branding.logoUrl ? (
            <img src={branding.logoUrl} alt={displayName} className={css.minimalLogo} />
          ) : (
            <AvatarLarge className={css.minimalAvatar} user={profileUser} disableProfileLink />
          )}
        </div>
        {heroHeading && <H2 as="h1" className={css.minimalName} style={headingStyle}>{heroHeading}</H2>}
        {effectiveHeadline && showName && displayName && (
          <p className={css.minimalSubName}>{displayName}</p>
        )}
        {metaBarPos === 'top' && metaBar}
        <SocialLinks socialLinks={branding.socialLinks} />
        <div className={css.minimalActions}>
          {profileUrl && <ProfileShareQR profileUrl={profileUrl} />}
          {showLinkToProfileSettingsPage && (
            <NamedLink className={css.heroEditLink} name="ProfileSettingsPage">
              <FormattedMessage id="ProfilePage.editProfileLinkDesktop" />
            </NamedLink>
          )}
        </div>
      </div>
      <div className={css.minimalContent}>
        {children}
        {metaBarPos === 'bottom' && metaBar}
      </div>
    </div>
  );
};

/* ── Card-grid (sidebar) layout ───────────────────────────────────────── */
const CardGridLayout = props => {
  const { branding, displayName, profileUser, showLinkToProfileSettingsPage, profileUrl, publicData, userFieldConfig, avgRating, reviewCount, isVerified, children } = props;
  const showHeadline = branding.showHeadline !== false;
  const showName = branding.showName !== false;
  const metaBarPos = branding.metaBarPosition || 'top';
  const headingStyle = {
    ...(branding.font ? { fontFamily: branding.font } : {}),
    ...(branding.headingFontBold ? { fontWeight: 700 } : {}),
  };
  const metaBar = <ProfileMetaBar publicData={publicData} userFieldConfig={userFieldConfig} avgRating={avgRating} reviewCount={reviewCount} isVerified={isVerified} />;
  return (
    <div className={css.cardGridRoot}>
      <div
        className={css.cardGridBanner}
        style={Object.keys(heroBgFromBranding(branding)).length ? heroBgFromBranding(branding) : { background: 'var(--brandPrimary, var(--marketplaceColor))' }}
      >
        {showHeadline && branding.headline && (
          <div
            className={css.cardGridBannerHeadline}
            style={{
              color: branding.headlineColor || '#ffffff',
              ...(branding.font ? { fontFamily: branding.font } : {}),
              ...(branding.headingFontBold ? { fontWeight: 700 } : {}),
            }}
          >
            {branding.headline}
          </div>
        )}
      </div>

      <div className={css.cardGridBody}>
        <div className={css.cardGridAside}>
          <div className={css.cardGridAsideInner}>
            <div className={css.logoContainer} style={{ ...logoSizeStyle(branding.logoSize), borderRadius: 12, overflow: 'hidden' }}>
              {branding.logoUrl ? (
                <img src={branding.logoUrl} alt={displayName} className={css.cardGridLogo} />
              ) : (
                <AvatarLarge className={css.cardGridAvatar} user={profileUser} disableProfileLink />
              )}
            </div>
            <div className={css.cardGridIdentity}>
              {showName && (
                <H2 as="h1" className={css.cardGridName} style={headingStyle}>
                  {displayName}
                </H2>
              )}
              {branding.tagline && <p className={css.cardGridTagline}>{branding.tagline}</p>}
            </div>
            {metaBarPos === 'top' && metaBar}
            <SocialLinks socialLinks={branding.socialLinks} />
            {profileUrl && <ProfileShareQR profileUrl={profileUrl} />}
            {metaBarPos === 'bottom' && metaBar}
            {showLinkToProfileSettingsPage && (
              <>
                <NamedLink className={css.cardGridEditLink} name="ProfileSettingsPage">
                  <FormattedMessage id="ProfilePage.editProfileLinkDesktop" />
                </NamedLink>
                <NamedLink className={css.brandingLink} name="BrandingSettingsPage">
                  ← Edit Branding
                </NamedLink>
              </>
            )}
          </div>
        </div>
        <div className={css.cardGridMain}>{children}</div>
      </div>
    </div>
  );
};

const CollapsibleTopbarToggle = () => {
  const [open, setOpen] = useState(false);
  return (
    <div className={css.collapsibleTopbar}>
      <button
        type="button"
        className={css.topbarToggleBtn}
        onClick={() => setOpen(v => !v)}
        aria-label={open ? 'Hide navigation' : 'Show navigation'}
        title={open ? 'Hide navigation' : 'Show GymNet navigation'}
      >
        {open ? '✕' : '☰'}
      </button>
      <div className={`${css.topbarDrawer} ${open ? css.topbarDrawerOpen : ''}`}>
        <TopbarContainer />
      </div>
    </div>
  );
};

const HeroLayout = props => {
  const { branding, displayName, profileUser, showLinkToProfileSettingsPage, profileUrl, publicData, userFieldConfig, avgRating, reviewCount, isVerified, children } = props;
  const showHeadline = branding.showHeadline !== false;
  const showName = branding.showName !== false;
  const metaBarPos = branding.metaBarPosition || 'top';
  const effectiveHeadline = showHeadline ? branding.headline : null;
  const heroHeading = effectiveHeadline || (showName ? displayName : null);
  const headingStyle = {
    color: branding.headlineColor || undefined,
    ...(branding.font ? { fontFamily: branding.font } : {}),
    ...(branding.headingFontBold ? { fontWeight: 700 } : {}),
  };
  const metaBar = <ProfileMetaBar publicData={publicData} userFieldConfig={userFieldConfig} avgRating={avgRating} reviewCount={reviewCount} isVerified={isVerified} />;

  return (
    <div className={css.heroRoot}>
      <div
        className={css.heroBanner}
        style={heroBgFromBranding(branding)}
      >
        {branding.logoUrl && (
          <div className={css.logoContainer} style={{ ...logoSizeStyle(branding.logoSize), position: 'absolute', top: 16, left: 20, borderRadius: 10, overflow: 'hidden', background: 'rgba(255,255,255,0.9)', padding: 4 }}>
            <img src={branding.logoUrl} alt={displayName} className={css.heroBannerLogo} />
          </div>
        )}
        {effectiveHeadline && (
          <div
            className={css.heroBannerHeadline}
            style={{
              color: branding.headlineColor || '#ffffff',
              ...(branding.font ? { fontFamily: branding.font } : {}),
              ...(branding.headingFontBold ? { fontWeight: 700 } : {}),
            }}
          >
            {effectiveHeadline}
          </div>
        )}
      </div>
      <div className={css.heroHeader}>
        <AvatarLarge className={css.heroAvatar} user={profileUser} disableProfileLink />
        <div className={css.heroMeta}>
          <div className={css.heroNameRow}>
            {heroHeading && <H2 as="h1" className={css.heroName} style={headingStyle}>{heroHeading}</H2>}
            {isVerified && metaBarPos !== 'hidden' && <VerifiedBadge size="md" showLabel />}
          </div>
          {effectiveHeadline && showName && displayName && (
            <p className={css.heroSubName}>{displayName}</p>
          )}
          {branding.tagline && (
            <p className={css.heroTagline}>{branding.tagline}</p>
          )}
          {metaBarPos === 'top' && metaBar}
          <SocialLinks socialLinks={branding.socialLinks} />
          <div className={css.heroActions}>
            {profileUrl && <ProfileShareQR profileUrl={profileUrl} />}
            {showLinkToProfileSettingsPage && (
              <NamedLink className={css.heroEditLink} name="ProfileSettingsPage">
                <FormattedMessage id="ProfilePage.editProfileLinkDesktop" />
              </NamedLink>
            )}
          </div>
        </div>
      </div>
      <div className={css.heroContent}>
        {children}
        {metaBarPos === 'bottom' && metaBar}
      </div>
    </div>
  );
};

/**
 * ProfilePageComponent
 */
export const ProfilePageComponent = props => {
  const config = useConfiguration();
  const intl = useIntl();
  const [mounted, setMounted] = useState(false);
  const [branding, setBranding] = useState(DEFAULT_BRANDING);
  const [brandingLoaded, setBrandingLoaded] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const {
    scrollingDisabled,
    params: pathParams,
    currentUser,
    useCurrentUser,
    userShowError,
    user,
    ...rest
  } = props;
  const isVariant = pathParams.variant?.length > 0;
  const isPreview = isVariant && pathParams.variant === PROFILE_PAGE_PENDING_APPROVAL_VARIANT;

  const searchParams = rest?.location?.search;
  const isStorefront = searchParams
    ? new URLSearchParams(searchParams)?.get('mode') === 'storefront'
    : false;
  if (isStorefront) {
    return <NamedRedirect name="LandingPage" />;
  }

  const isCurrentUser = currentUser?.id && currentUser?.id?.uuid === pathParams.id;
  const profileUser = useCurrentUser ? currentUser : user;
  const { bio, displayName, publicData, metadata } = profileUser?.attributes?.profile || {};
  const { userFields } = config.user;
  const isPrivateMarketplace = config.accessControl.marketplace.private === true;
  const isUnauthorizedUser = currentUser && !isUserAuthorized(currentUser);
  const isUnauthorizedOnPrivateMarketplace = isPrivateMarketplace && isUnauthorizedUser;
  const hasUserPendingApprovalError = isErrorUserPendingApproval(userShowError);
  const hasNoViewingRightsUser = currentUser && !hasPermissionToViewData(currentUser);
  const hasNoViewingRightsOnPrivateMarketplace = isPrivateMarketplace && hasNoViewingRightsUser;

  const isDataLoaded = isPreview
    ? currentUser != null || userShowError != null
    : hasNoViewingRightsOnPrivateMarketplace
    ? currentUser != null || userShowError != null
    : user != null || userShowError != null;

  const schemaTitleVars = { name: displayName, marketplaceName: defaultConfig.marketplaceName };
  const schemaTitle = intl.formatMessage({ id: 'ProfilePage.schemaTitle' }, schemaTitleVars);

  const [endorsements, setEndorsements] = useState([]);
  const [endorsementCount, setEndorsementCount] = useState(0);
  const uuid = profileUser?.id?.uuid;
  const isVerified = useVerifiedStatus(uuid);

  useEffect(() => {
    const fetchEndorsements = async () => {
      try {
        const res = await fetch(`/api/endorsements/user/${uuid}`);
        if (!res.ok) throw new Error(`Failed to fetch endorsements, status: ${res.status}`);
        const data = await res.json();
        setEndorsements(data);
        setEndorsementCount(data.length);
      } catch (err) {
        console.error('Error fetching endorsements:', err);
      }
    };
    if (uuid) fetchEndorsements();
  }, [uuid]);

  useEffect(() => {
    if (!document.querySelector(`link[href="${FONT_GFONTS_URL}"]`)) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = FONT_GFONTS_URL;
      document.head.appendChild(link);
    }
  }, []);

  useEffect(() => {
    if (!uuid) return;
    fetch(`/api/profile-branding/public/${uuid}`)
      .then(r => r.json())
      .then(data => {
        if (data.branding) setBranding({ ...DEFAULT_BRANDING, ...data.branding });
        setBrandingLoaded(true);
      })
      .catch(() => setBrandingLoaded(true));
  }, [uuid]);

  if (userShowError && userShowError.status === 404) {
    return <NotFoundPage />;
  }

  if (!isDataLoaded) {
    return null;
  } else if (!isPreview && isNotFoundError(userShowError)) {
    return <NotFoundPage staticContext={props.staticContext} />;
  } else if (!isPreview && (isUnauthorizedOnPrivateMarketplace || hasUserPendingApprovalError)) {
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_USER_PENDING_APPROVAL }}
      />
    );
  } else if (
    (!isPreview && hasNoViewingRightsOnPrivateMarketplace && !isCurrentUser) ||
    isErrorNoViewingPermission(userShowError)
  ) {
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_VIEW_LISTINGS }}
      />
    );
  } else if (!isPreview && isForbiddenError(userShowError)) {
    return (
      <NamedRedirect
        name="SignupPage"
        state={{ from: `${location.pathname}${location.search}${location.hash}` }}
      />
    );
  } else if (isPreview && mounted && !isCurrentUser) {
    return isCurrentUser === false ? (
      <NamedRedirect name="ProfilePage" params={{ id: currentUser?.id?.uuid }} />
    ) : null;
  } else if ((isPreview || isPrivateMarketplace) && !mounted) {
    return null;
  }

  const themePreset = branding.theme ? THEMES[branding.theme] : null;
  const brandingVars = {
    ...(themePreset
      ? themeToVars(themePreset)
      : branding.primaryColor
      ? {
          '--brandPrimary': branding.primaryColor,
          '--brandAccent': branding.accentColor,
          '--marketplaceColor': branding.primaryColor,
          '--marketplaceColorDark': branding.accentColor,
          '--marketplaceColorLight': branding.primaryColor + '33',
        }
      : {}),
    ...(branding.backgroundColor ? { background: branding.backgroundColor } : {}),
    ...(branding.textColor ? { color: branding.textColor, '--brandText': branding.textColor } : {}),
    ...(branding.font ? { fontFamily: branding.font, '--brandFont': branding.font } : {}),
    ...(branding.buttonTextColor ? { '--brandButtonText': branding.buttonTextColor } : {}),
  };

  const isHero = branding.layout === 'hero';
  const isMinimal = branding.layout === 'minimal';
  const isCardGrid = branding.layout === 'card-grid';

  // Wait until branding is fetched before committing to a layout — prevents
  // the default sidebar flashing before the correct layout is known.
  if (uuid && !brandingLoaded) return null;

  const topbarHidden = mounted && !isCurrentUser && branding.showTopbar === false && branding.layout !== null;
  const footerHidden = mounted && branding.showFooter === false;
  const footerNode = footerHidden ? null : <FooterContainer />;

  const providerReviews = (rest.reviews || []).filter(r => r.attributes?.type === REVIEW_TYPE_OF_PROVIDER);
  const avgRating = providerReviews.length > 0
    ? providerReviews.reduce((sum, r) => sum + (r.attributes?.rating || 0), 0) / providerReviews.length
    : null;
  const reviewCount = providerReviews.length;

  const sharedContentProps = {
    bio,
    displayName,
    uuid,
    userShowError,
    endorsementCount,
    endorsements,
    publicData,
    metadata,
    userFieldConfig: userFields,
    hideReviews: hasNoViewingRightsOnPrivateMarketplace,
    intl,
    branding,
    avgRating,
    reviewCount,
    ...rest,
  };

  const topbarNode = topbarHidden ? null : <TopbarContainer />;
  const profileUrl = typeof window !== 'undefined' ? window.location.href : (uuid ? `/u/${uuid}` : '');

  // Inject scoped style for button text color — CSS modules can't reliably override
  // global button classes, so we use a stable page ID + injected <style> tag instead.
  const PROFILE_PAGE_ID = 'branded-profile-page';
  const buttonTextStyle = branding.buttonTextColor
    ? `#${PROFILE_PAGE_ID} .buttonDefault,#${PROFILE_PAGE_ID} .buttonPrimary,#${PROFILE_PAGE_ID} .buttonPrimaryInline{color:${branding.buttonTextColor}!important}#${PROFILE_PAGE_ID} .buttonDefault:hover,#${PROFILE_PAGE_ID} .buttonPrimary:hover,#${PROFILE_PAGE_ID} .buttonPrimaryInline:hover{color:${branding.buttonTextColor}!important}`
    : null;

  const ownerEditBar = mounted && isCurrentUser ? (
    <div className={css.ownerEditBar}>
      <span className={css.ownerEditBarLabel}>You're viewing your public profile</span>
      <NamedLink className={css.ownerEditBarBtn} name="BrandingSettingsPage">
        ← Edit Branding
      </NamedLink>
    </div>
  ) : null;

  const sharedLayoutProps = {
    branding,
    displayName,
    profileUser,
    showLinkToProfileSettingsPage: mounted && isCurrentUser,
    profileUrl,
    publicData,
    userFieldConfig: userFields,
    avgRating,
    reviewCount,
    isVerified,
  };

  if (isHero) {
    return (
      <Page
        scrollingDisabled={scrollingDisabled}
        title={schemaTitle}
        schema={{ '@context': 'http://schema.org', '@type': 'ProfilePage', name: schemaTitle }}
      >
        <div id={PROFILE_PAGE_ID} style={brandingVars} className={[css.brandedProfile, branding.font ? css.brandedFont : ''].filter(Boolean).join(' ')}>
          {buttonTextStyle && <style>{buttonTextStyle}</style>}
          {topbarHidden && <CollapsibleTopbarToggle />}
          {ownerEditBar}
          <LayoutSingleColumn topbar={topbarNode} footer={footerNode}>
            <HeroLayout {...sharedLayoutProps}>
              <TabbedMainContent {...sharedContentProps} />
            </HeroLayout>
          </LayoutSingleColumn>
        </div>
      </Page>
    );
  }

  if (isMinimal) {
    return (
      <Page
        scrollingDisabled={scrollingDisabled}
        title={schemaTitle}
        schema={{ '@context': 'http://schema.org', '@type': 'ProfilePage', name: schemaTitle }}
      >
        <div id={PROFILE_PAGE_ID} style={brandingVars} className={[css.brandedProfile, branding.font ? css.brandedFont : ''].filter(Boolean).join(' ')}>
          {buttonTextStyle && <style>{buttonTextStyle}</style>}
          {topbarHidden && <CollapsibleTopbarToggle />}
          {ownerEditBar}
          <LayoutSingleColumn topbar={topbarNode} footer={footerNode}>
            <MinimalLayout {...sharedLayoutProps}>
              <TabbedMainContent {...sharedContentProps} />
            </MinimalLayout>
          </LayoutSingleColumn>
        </div>
      </Page>
    );
  }

  if (isCardGrid) {
    return (
      <Page
        scrollingDisabled={scrollingDisabled}
        title={schemaTitle}
        schema={{ '@context': 'http://schema.org', '@type': 'ProfilePage', name: schemaTitle }}
      >
        <div id={PROFILE_PAGE_ID} style={brandingVars} className={[css.brandedProfile, branding.font ? css.brandedFont : ''].filter(Boolean).join(' ')}>
          {buttonTextStyle && <style>{buttonTextStyle}</style>}
          {topbarHidden && <CollapsibleTopbarToggle />}
          {ownerEditBar}
          <LayoutSingleColumn topbar={topbarNode} footer={footerNode}>
            <CardGridLayout {...sharedLayoutProps}>
              <TabbedMainContent {...sharedContentProps} />
            </CardGridLayout>
          </LayoutSingleColumn>
        </div>
      </Page>
    );
  }

  return (
    <Page
      scrollingDisabled={scrollingDisabled}
      title={schemaTitle}
      schema={{ '@context': 'http://schema.org', '@type': 'ProfilePage', name: schemaTitle }}
    >
      <div id={PROFILE_PAGE_ID} style={brandingVars} className={[css.brandedProfile, branding.font ? css.brandedFont : ''].filter(Boolean).join(' ')}>
        {buttonTextStyle && <style>{buttonTextStyle}</style>}
        {topbarHidden && <CollapsibleTopbarToggle />}
        {ownerEditBar}
        <LayoutSideNavigation
          sideNavClassName={css.aside}
          topbar={topbarNode}
          sideNav={
            <AsideContent
              user={profileUser}
              showLinkToProfileSettingsPage={mounted && isCurrentUser}
              displayName={displayName}
              branding={branding}
              profileUrl={profileUrl}
            />
          }
          footer={footerNode}
        >
          {branding.layout === 'card-grid' ? (
            <TabbedMainContent {...sharedContentProps} />
          ) : (
            <MainContent
              bio={bio}
              displayName={displayName}
              uuid={uuid}
              userShowError={userShowError}
              endorsementCount={endorsementCount}
              endorsements={endorsements}
              publicData={publicData}
              metadata={metadata}
              userFieldConfig={userFields}
              hideReviews={hasNoViewingRightsOnPrivateMarketplace}
              intl={intl}
              {...rest}
            />
          )}
        </LayoutSideNavigation>
      </div>
    </Page>
  );
};

const mapStateToProps = state => {
  const { currentUser } = state.user;
  const {
    userId,
    userShowError,
    queryListingsError,
    userListingRefs,
    reviews = [],
    queryReviewsError,
  } = state.ProfilePage;
  const userMatches = getMarketplaceEntities(state, [{ type: 'user', id: userId }]);
  const user = userMatches.length === 1 ? userMatches[0] : null;

  const isCurrentUser = userId?.uuid === currentUser?.id?.uuid;
  const useCurrentUser =
    isCurrentUser && !(isUserAuthorized(currentUser) && hasPermissionToViewData(currentUser));

  return {
    scrollingDisabled: isScrollingDisabled(state),
    currentUser,
    useCurrentUser,
    user,
    userShowError,
    queryListingsError,
    listings: getMarketplaceEntities(state, userListingRefs),
    reviews,
    queryReviewsError,
  };
};

const ProfilePage = compose(connect(mapStateToProps))(ProfilePageComponent);

export default ProfilePage;
